import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import BasePageLayout from "../../components/layouts/base-page-layout/base-page-layout";
import emailCustomizationStore from "./stores/email-customization-store";
import LogoPreview from "./components/logo-preview/logo-preview";
import currentPortalStore from "../../stores/current-portal-store";
import { IOption, SelectDropdown } from "../../components/inputs/select-dropdown/select-dropdown";
import { FormFieldLine } from "../../components/form-field-line/form-field-line";
import Hr from "../../components/hr/hr";
import EditorPageLayout from "../../components/layouts/editor-page/editor-page-layout";
import { SingleValue } from "react-select";
import LogoUpload from "./components/logo-upload/logo-upload";
import EmailColorstEditor from "./components/email-colors-editor/email-colors-editor";
import './email-look-and-feel-page.scss';
import { ConfirmModal } from "../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../hooks/navigation-blocker-hook";

const EmailLookAndFeelPage: React.FC = observer(() => {
    useEffect(() => {
        currentPortalStore.load();
        emailCustomizationStore.load();
    }, []);

    const logoAlignmentOptions = [
        { label: 'Center', value: 'center' },
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
    ];

    const hasChanges = (): boolean => {
        return emailCustomizationStore?.hasChanges() || false;
    }

    const setLogoAlign = (v: SingleValue<IOption>) => {
        if (v?.value) {
            emailCustomizationStore.setLogoAlign(v.value as string);
        }
    }

    const discardChanges = () => {
        emailCustomizationStore?.discardChanges();
    }

    const saveChanges = async () => {
        await emailCustomizationStore.save();
        unblockNavigation();
    }

    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    return (
        <BasePageLayout
            title="Settings"
            subTitles={['Emails', 'Look and Feel']}
            instruction={
                <>
                    Customize the look and feel of emails that are sent to customers.
                </>
            }
        >
            <EditorPageLayout
                toolbar={{
                    hasLocalesSelector: false,
                    hasBack: false,
                    saveButton: {
                        hasChanges: hasChanges(),
                        onClick: saveChanges,
                        disabled: !hasChanges() || emailCustomizationStore?.isSaving(),
                    },
                    isLoading: emailCustomizationStore?.isSaving() || emailCustomizationStore.isLoading
                }}>
                <div className="email-look-and-feel">
                    <LogoUpload />
                    <FormFieldLine
                        label='Alignment'
                        note='Logo alignment on emails.'
                    >
                        <SelectDropdown
                            options={logoAlignmentOptions}
                            value={logoAlignmentOptions.find(o => o.value === emailCustomizationStore.logoAlign)}
                            onChange={setLogoAlign}
                            disabled={emailCustomizationStore.isLoading || emailCustomizationStore.isSaving()}
                        />
                    </FormFieldLine>
                    <LogoPreview />
                    <Hr />
                    <EmailColorstEditor />
                </div>
                {isShowUnsaved &&
                    <ConfirmModal
                        cancelButton={{
                            title: 'Cancel',
                            onClick: () => setIsShowUnsaved(false)
                        }}
                        okButton={{
                            title: 'Discard',
                            onClick: () => {
                                discardChanges();
                                setIsShowUnsaved(false);
                                retryNavigation();
                            }
                        }}
                    />
                }
            </EditorPageLayout>

        </BasePageLayout>
    );
});

export default EmailLookAndFeelPage;